import { Controller } from "@hotwired/stimulus";

type WidthOptions = {
    persist?: boolean;
};

type PaneKind = "chat" | "list";

type WidthBounds = {
    min: number;
    max: number;
};

const CHAT_STORAGE_KEY = "tickets.chatPaneWidth";
const LIST_STORAGE_KEY = "tickets.listPaneWidth";
const STORED_CHAT_WIDTH_PROPERTY = "--tickets-chat-pane-stored-width";
const STORED_LIST_WIDTH_PROPERTY = "--tickets-list-pane-stored-width";
const CHAT_WIDTH_PROPERTY = "--tickets-chat-pane-width";
const LIST_WIDTH_PROPERTY = "--tickets-list-pane-width";
const KEYBOARD_STEP = 32;
const DEFAULT_LIST_WIDTH = 288;

export default class extends Controller<HTMLElement> {
    static targets = ["chat", "detail", "divider", "list", "listDivider", "workspace"];

    declare readonly chatTarget: HTMLElement;
    declare readonly detailTarget: HTMLElement;
    declare readonly dividerTarget: HTMLElement;
    declare readonly listTarget: HTMLElement;
    declare readonly listDividerTarget: HTMLElement;
    declare readonly workspaceTarget: HTMLElement;
    declare readonly hasChatTarget: boolean;
    declare readonly hasDividerTarget: boolean;
    declare readonly hasListTarget: boolean;
    declare readonly hasListDividerTarget: boolean;
    declare readonly hasWorkspaceTarget: boolean;

    private drag: PaneKind | null = null;
    private hasStoredChatWidth = false;
    private hasStoredListWidth = false;
    private resizeObserver: ResizeObserver | null = null;

    connect(): void {
        this.drag = null;
        this.move = this.move.bind(this);
        this.stop = this.stop.bind(this);
        this.reconcile = this.reconcile.bind(this);

        const storedListWidth = this.storedWidth(LIST_STORAGE_KEY);
        this.hasStoredListWidth = storedListWidth !== null;
        if (storedListWidth) {
            this.applyListWidth(storedListWidth);
        }

        const storedChatWidth = this.storedWidth(CHAT_STORAGE_KEY);
        this.hasStoredChatWidth = storedChatWidth !== null;
        if (storedChatWidth) {
            this.applyChatWidth(storedChatWidth);
        }

        this.resizeObserver = new ResizeObserver(this.reconcile);
        this.resizeObserver.observe(this.element);
        this.reconcile();
    }

    disconnect(): void {
        this.stop();
        this.resizeObserver?.disconnect();
    }

    start(event: PointerEvent): void {
        this.startDrag(event, "chat");
    }

    startList(event: PointerEvent): void {
        this.startDrag(event, "list");
    }

    private startDrag(event: PointerEvent, pane: PaneKind): void {
        if (event.button !== 0) {
            return;
        }

        event.preventDefault();
        this.drag = pane;
        this.dividerForPane(pane)?.setPointerCapture?.(event.pointerId);
        document.documentElement.classList.add("select-none");
        document.documentElement.style.cursor = "col-resize";
        window.addEventListener("pointermove", this.move);
        window.addEventListener("pointerup", this.stop, { once: true });
    }

    private move(event: PointerEvent): void {
        if (!this.drag) {
            return;
        }

        if (this.drag === "list") {
            this.resizeListFromClientX(event.clientX);
        } else {
            this.resizeChatFromClientX(event.clientX);
        }
    }

    private stop(): void {
        if (!this.drag) {
            return;
        }

        this.drag = null;
        document.documentElement.classList.remove("select-none");
        document.documentElement.style.cursor = "";
        window.removeEventListener("pointermove", this.move);
        window.removeEventListener("pointerup", this.stop);
    }

    keydown(event: KeyboardEvent): void {
        this.resizeChatFromKeyboard(event);
    }

    keydownList(event: KeyboardEvent): void {
        this.resizeListFromKeyboard(event);
    }

    private resizeChatFromKeyboard(event: KeyboardEvent): void {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
            return;
        }

        event.preventDefault();
        const currentWidth = this.chatTarget.getBoundingClientRect().width;
        const delta = event.key === "ArrowLeft" ? KEYBOARD_STEP : -KEYBOARD_STEP;
        this.applyChatWidth(currentWidth + delta);
    }

    private resizeListFromKeyboard(event: KeyboardEvent): void {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
            return;
        }

        event.preventDefault();
        const currentWidth = this.listTarget.getBoundingClientRect().width;
        const delta = event.key === "ArrowRight" ? KEYBOARD_STEP : -KEYBOARD_STEP;
        this.applyListWidth(currentWidth + delta);
    }

    private reconcile(): void {
        this.reconcileListWidth();
        this.reconcileChatWidth();
    }

    private reconcileListWidth(): void {
        if (!this.hasListTarget || !this.hasListDividerTarget) {
            return;
        }

        const currentWidth = this.listTarget.getBoundingClientRect().width || this.defaultListWidth();
        const width = this.hasStoredListWidth ? currentWidth : this.defaultListWidth();
        this.applyListWidth(width, { persist: false });
    }

    private reconcileChatWidth(): void {
        if (!this.hasChatTarget || !this.hasDividerTarget) {
            return;
        }

        const currentWidth = this.chatTarget.getBoundingClientRect().width || this.defaultChatWidth();
        const width = this.hasStoredChatWidth ? currentWidth : this.defaultChatWidth();
        this.applyChatWidth(width, { persist: false });
    }

    private resizeChatFromClientX(clientX: number): void {
        const containerRect = this.chatContainer().getBoundingClientRect();
        const rawChatWidth = containerRect.right - clientX - this.dividerTarget.offsetWidth / 2;
        this.applyChatWidth(rawChatWidth);
    }

    private resizeListFromClientX(clientX: number): void {
        const containerRect = this.element.getBoundingClientRect();
        const rawListWidth = clientX - containerRect.left - this.listDividerTarget.offsetWidth / 2;
        this.applyListWidth(rawListWidth);
    }

    private applyChatWidth(rawWidth: number, options: WidthOptions = {}): void {
        if (!this.hasChatTarget || !this.hasDividerTarget) {
            return;
        }

        const { persist = true } = options;
        const width = this.clampChatWidth(rawWidth);

        this.element.style.setProperty(CHAT_WIDTH_PROPERTY, `${width}px`);
        if (this.hasStoredChatWidth || persist) {
            document.documentElement.style.setProperty(STORED_CHAT_WIDTH_PROPERTY, `${width}px`);
        }
        this.setSeparatorValue(this.dividerTarget, width, this.chatWidthBounds());

        if (persist) {
            this.hasStoredChatWidth = true;
            window.localStorage.setItem(CHAT_STORAGE_KEY, Math.round(width).toString());
        }
    }

    private applyListWidth(rawWidth: number, options: WidthOptions = {}): void {
        if (!this.hasListTarget || !this.hasListDividerTarget) {
            return;
        }

        const { persist = true } = options;
        const width = this.clampListWidth(rawWidth);

        this.element.style.setProperty(LIST_WIDTH_PROPERTY, `${width}px`);
        if (this.hasStoredListWidth || persist) {
            document.documentElement.style.setProperty(STORED_LIST_WIDTH_PROPERTY, `${width}px`);
        }
        this.setSeparatorValue(this.listDividerTarget, width, this.listWidthBounds());

        if (persist) {
            this.hasStoredListWidth = true;
            window.localStorage.setItem(LIST_STORAGE_KEY, Math.round(width).toString());
        }

        this.reconcileChatWidth();
    }

    private clampChatWidth(rawWidth: number): number {
        const bounds = this.chatWidthBounds();
        if (!bounds) {
            return rawWidth;
        }

        return Math.min(Math.max(rawWidth, bounds.min), bounds.max);
    }

    private clampListWidth(rawWidth: number): number {
        const bounds = this.listWidthBounds();
        if (!bounds) {
            return rawWidth;
        }

        return Math.min(Math.max(rawWidth, bounds.min), bounds.max);
    }

    private chatWidthBounds(): WidthBounds | null {
        const containerWidth = this.chatContainer().getBoundingClientRect().width;
        const dividerWidth = this.dividerTarget.offsetWidth || 0;

        if (containerWidth <= 0) {
            return null;
        }

        const minChatWidth = Math.max(144, Math.min(288, containerWidth * 0.28));
        const minDetailWidth = Math.max(220, Math.min(384, containerWidth * 0.48));
        const maxChatWidth = Math.max(
            minChatWidth,
            containerWidth - minDetailWidth - dividerWidth
        );

        return { min: minChatWidth, max: maxChatWidth };
    }

    private listWidthBounds(): WidthBounds | null {
        const containerWidth = this.element.getBoundingClientRect().width;
        const dividerWidth = this.listDividerTarget.offsetWidth || 0;

        if (containerWidth <= 0) {
            return null;
        }

        const minListWidth = Math.max(224, Math.min(320, containerWidth * 0.18));
        const minWorkspaceWidth = Math.max(520, Math.min(760, containerWidth * 0.55));
        const maxListWidth = Math.max(
            minListWidth,
            containerWidth - minWorkspaceWidth - dividerWidth
        );

        return { min: minListWidth, max: maxListWidth };
    }

    private setSeparatorValue(
        separator: HTMLElement,
        width: number,
        bounds: WidthBounds | null
    ): void {
        separator.setAttribute("aria-valuenow", Math.round(width).toString());
        if (!bounds) {
            return;
        }

        separator.setAttribute("aria-valuemin", Math.round(bounds.min).toString());
        separator.setAttribute("aria-valuemax", Math.round(bounds.max).toString());
    }

    private defaultChatWidth(): number {
        const containerWidth = this.chatContainer().getBoundingClientRect().width;
        return Math.min(Math.max(containerWidth * 0.35, 144), 384);
    }

    private defaultListWidth(): number {
        return DEFAULT_LIST_WIDTH;
    }

    private chatContainer(): HTMLElement {
        return this.hasWorkspaceTarget ? this.workspaceTarget : this.element;
    }

    private dividerForPane(pane: PaneKind): HTMLElement | null {
        if (pane === "list") {
            return this.hasListDividerTarget ? this.listDividerTarget : null;
        }

        return this.hasDividerTarget ? this.dividerTarget : null;
    }

    private storedWidth(key: string): number | null {
        const value = Number.parseInt(window.localStorage.getItem(key) || "", 10);
        return Number.isFinite(value) ? value : null;
    }
}
